import { useState, type ReactNode } from 'react';
import { PlatformAgreementDialog } from '~/features/terms/PlatformAgreementDialog';
import { TERMS_DOCUMENT } from '~/features/terms/platformParticipationAgreement';
import { IdentityGatePanel, MarketStatusPill, type MarketAction } from './index';
import type { ResponseUnmaskState, UnmaskAction } from './responseUnmask.api';

// [MCV-1755] The message leg of eligibility counts turns, not messages: a run of
// consecutive messages from one side is a single turn. Capping each figure at its
// threshold keeps a long one-sided run from reading as met ("7/5").
const progress = (count: number, threshold: number) => `${Math.min(count, threshold)}/${threshold}`;

const MaskedProgress = ({ state }: { state: ResponseUnmaskState }) => {
  // An older API omits messageTurnCount; fall back to the raw message count.
  const messages =
    state.messageTurnCount == null
      ? `Messages: ${progress(state.messageCount, state.messageThreshold)}`
      : `Back-and-forth turns: ${progress(state.messageTurnCount, state.messageThreshold)}`;
  // Either party can ask once the thresholds are met; the other has to approve.
  const requirement = `Identities remain masked. To request consent, reach ${state.messageThreshold} back-and-forth turns or ${state.negotiationThreshold} negotiation versions. Consecutive messages from the same side count as one turn.`;
  return (
    <>
      <span data-cy="response-unmask-requirement" className="block">
        {requirement}
      </span>
      <span data-cy="response-unmask-progress" className="mt-1 block text-sm">
        <span className="block">{messages}</span>
        <span className="block">
          {`Negotiation versions: ${progress(state.negotiationCount, state.negotiationThreshold)}`}
        </span>
      </span>
    </>
  );
};

type AgreementStep = { action: 'request' | 'approve'; shownToken: string | null };

const AGREEMENT_COPY = {
  request: {
    description:
      'By requesting, you ask the other party to share identities for this response. Nothing is shared unless they approve. Accepting the agreement is part of the request.',
    primaryLabel: 'Accept and request disclosure',
  },
  approve: {
    description:
      "By approving, you consent to share your identity and contact details, and those of providers attached now or added later, with the other party on this response. Both sides will be unmasked and this can't be undone. Accepting the agreement is part of the approval.",
    primaryLabel: 'Accept and approve disclosure',
  },
} as const;

export const ResponseUnmaskControls = ({
  state,
  busy,
  disabled = false,
  error,
  onAction,
  onRefresh,
}: {
  state: ResponseUnmaskState | null;
  busy: boolean;
  disabled?: boolean;
  error: string | null;
  onAction: (
    action: UnmaskAction,
    expectedToken?: string | null,
    termsVersion?: string | null
  ) => void;
  onRefresh: () => void;
}) => {
  // [MCV-1755] Requesting and approving both open the Platform Participation Agreement.
  // Approving unmasks both sides and cannot be undone, so it was already a deliberate
  // second step; the agreement dialog is that step. Cancel just closes it; only the
  // accept button reaches the action.
  const [agreement, setAgreement] = useState<AgreementStep | null>(null);
  const openAgreement = (action: AgreementStep['action']) =>
    // The request the user was looking at when the dialog opened; the machine
    // refuses to approve if a refresh swapped it before they accepted.
    setAgreement({ action, shownToken: state?.requestToken ?? null });
  const acceptAgreement = () => {
    if (!agreement) return;
    const { action, shownToken } = agreement;
    setAgreement(null);
    onAction(action, action === 'approve' ? shownToken : undefined, TERMS_DOCUMENT.version);
  };
  const actions: MarketAction[] = [];
  const add = (action: UnmaskAction, label: string, enabled: boolean) => {
    if (enabled)
      actions.push({
        label,
        cyTag: `btn-${action}-unmask`,
        disabled: busy || disabled,
        onClick:
          action === 'approve' || action === 'request'
            ? () => openAgreement(action)
            : () => onAction(action),
        colorVariant:
          action === 'reject' ? 'error' : action === 'withdraw' ? 'tertiary' : 'primary',
      });
  };
  if (state) {
    add('request', 'Request unmask', state.canRequest);
    add('approve', 'Approve', state.canApprove);
    add('reject', 'Reject', state.canReject);
    add('withdraw', 'Withdraw request', state.canWithdraw);
  }
  if (error)
    actions.push({
      label: 'Refresh identity state',
      onClick: onRefresh,
      disabled: busy,
      colorVariant: 'tertiary',
    });
  // A pending request that cannot be approved: it was made under the old rules, or before the
  // agreement existed (no acceptance on record). Whoever may request asks again; canWithdraw only
  // says whether this viewer made it; a colleague on either side sees the neutral wording.
  const legacyRequest =
    state?.privacyStatus === 'PENDING' && state.requesterIsListingOwner === false;
  const description: ReactNode = !state ? (
    'Loading identity permissions.'
  ) : state.isUnmasked ? (
    'Identities are shared for this response only.'
  ) : legacyRequest ? (
    state.canRequest ? (
      state.canWithdraw ? (
        'Your earlier request was made before the Platform Participation Agreement. Request disclosure again, accepting the agreement, to continue.'
      ) : (
        'The pending request was made under the old rules and cannot be approved. Request disclosure yourself, accepting the agreement, to continue.'
      )
    ) : state.canWithdraw ? (
      'You asked to share identities under the old rules. Withdraw yours so identity disclosure can be requested again.'
    ) : (
      'The pending request was made under the old rules and cannot be approved. Wait for the other party to request disclosure.'
    )
  ) : state.privacyStatus === 'PENDING' ? (
    'Identity-sharing request pending. Approving shares permitted identities and details for both sides of this response, including providers attached now or added later. It does not accept an offer or reserve availability.'
  ) : state.eligible ? (
    state.canRequest ? (
      'Identities remain masked. You can ask the other party to share identities for this response.'
    ) : (
      'Identities remain masked. Identity disclosure can now be requested.'
    )
  ) : (
    <MaskedProgress state={state} />
  );
  return (
    <div className="mb-5 text-left" data-cy="response-unmask-controls">
      <IdentityGatePanel
        title="Identity access"
        description={description}
        actions={actions}
        status={
          <MarketStatusPill
            status={
              state?.isUnmasked
                ? 'unmasked'
                : state?.privacyStatus === 'PENDING'
                  ? 'pending'
                  : 'masked'
            }
            label={
              state?.isUnmasked
                ? 'Unmasked'
                : state?.privacyStatus === 'PENDING'
                  ? 'Pending'
                  : 'Masked'
            }
          />
        }
      />
      <p role="status" aria-live="polite" className="mt-2 min-h-5 text-left text-sm">
        {busy ? 'Updating identity permissions…' : (error ?? '')}
      </p>
      <PlatformAgreementDialog
        open={agreement !== null}
        description={agreement ? AGREEMENT_COPY[agreement.action].description : undefined}
        primaryLabel={
          agreement ? AGREEMENT_COPY[agreement.action].primaryLabel : 'Accept and continue'
        }
        secondaryLabel="Cancel"
        onAccept={acceptAgreement}
        onSecondary={() => setAgreement(null)}
      />
    </div>
  );
};
